import { getDefaultStore } from 'jotai'

import { BffInterfacesBasket } from '@dfds-pax/booking-experience-bff'
import { seabookRouteMap } from '@dfds-pax/utils'

import { getPrioritizedOffer } from '@/app/[locale]/[ferry]/_actions/getPrioritizedOffer'
import { HandleRecoveredBasket } from '@/components/yourbooking/RecoverBasketHandler'
import {
  selectedDateAtom,
  selectedOfferAtom,
  selectedPassengersAtom,
  selectedPetsAtom,
  selectedRoutesAtom,
  selectedVehiclesAtom,
} from '@/states/selectionAtom'

import {
  CommercialDiscount,
  getSessionBasket,
  getUrlLocale,
  isCommercialDiscount,
  isDateInThePast,
  setSession,
  setSessionCommercialDiscount,
} from '../utils'
const presentDay = new Date().toISOString().substring(0, 10)

const toLegacyRoute = (value: string | null): string | null => {
  if (!value?.includes('-')) return value

  return seabookRouteMap.find(({ route }) => route === value)?.seabook ?? value
}

export const updateYourBookingUrl = async (): Promise<{
  commercialDiscount: CommercialDiscount | null
  depDateHomebound: string | null
  recoveredBasketErrors: BffInterfacesBasket.RecoveredBasketErrors | null
  routeHomebound: string | null
}> => {
  let homeboundDepartureDate = null
  let homeboundRoute = null
  let recoveredBasketErrors = null
  let recoveredCommercialDiscount: CommercialDiscount | null = null

  const store = getDefaultStore()
  const urlParams = new URLSearchParams(window.location.search)
  let depDateOut = urlParams.get('depDateOut')
  let depDateHome = urlParams.get('depDateHome')
  const routeOut = toLegacyRoute(urlParams.get('routeOut'))
  const routeHome = toLegacyRoute(urlParams.get('routeHome'))
  const vehicleType = urlParams.get('vty')
  const trailerType = urlParams.get('tty')
  const numberOfAdults = urlParams.get('adl')
  const childrenAges = urlParams.getAll('chdAges')
  const showNumberOfPets = urlParams.get('pets')
  const basketId = urlParams.get('basketId')
  const sessionId = urlParams.get('sessionId')
  const recoveredBasketErrorsParam = urlParams.get('recoveredBasketErrors')
  const offerCode = urlParams.get('offer') as string | undefined
  const minibookId = urlParams.get('minibookId')
  const travelSearchId = urlParams.get('travelSearchId')
  const commercialDiscountParam = urlParams.get('commercialDiscount')
  const commercialDiscount = isCommercialDiscount(commercialDiscountParam)
    ? (commercialDiscountParam.toLowerCase() as CommercialDiscount)
    : null

  const selectedRoutes = store.get(selectedRoutesAtom)
  const selectedDates = store.get(selectedDateAtom)
  const selectedPets = store.get(selectedPetsAtom)

  if (basketId && sessionId) {
    const { commercialDiscount: recoveredDiscount, homeDepDate, homeRoute } = await HandleRecoveredBasket(basketId)
    recoveredBasketErrors = recoveredBasketErrorsParam && JSON.parse(recoveredBasketErrorsParam)
    setSession(sessionId)
    homeboundDepartureDate = homeDepDate
    homeboundRoute = homeRoute
    recoveredCommercialDiscount = recoveredDiscount
  } else if (routeOut) {
    // Route
    if (routeOut && (!selectedRoutes.outbound || !selectedRoutes.homebound)) {
      store.set(selectedRoutesAtom, {
        homebound: routeHome === 'ONE_WAY' ? null : routeHome,
        outbound: routeOut,
      })
      homeboundRoute = routeHome === 'ONE_WAY' ? null : routeHome
    }
    // Date
    let depDateReturn = routeHome === 'ONE_WAY' ? null : depDateHome
    if (depDateReturn && isDateInThePast(depDateReturn)) depDateReturn = presentDay
    homeboundDepartureDate = depDateReturn
    if (!depDateOut || depDateOut === 'undefined' || isDateInThePast(depDateOut)) depDateOut = presentDay
    store.set(selectedDateAtom, {
      ...store.get(selectedDateAtom),
      outboundDepartureDate: depDateOut,
      returnDepartureDate: depDateReturn,
    })
    // Passengers
    const numberOfChildren = childrenAges.filter((a) => Number(a) > 3 && Number(a) < 16).length
    const numberOfInfants = childrenAges.filter((a) => Number(a) >= 0 && Number(a) < 4).length
    const childrenAgesNum = childrenAges.map((a) => Number(a))

    store.set(selectedPassengersAtom, {
      ...store.get(selectedPassengersAtom),
      adults: Number(numberOfAdults),
      children: numberOfChildren,
      childrenAges: childrenAgesNum,
      infants: numberOfInfants,
    })
    // Vehicle
    if (vehicleType) {
      store.set(selectedVehiclesAtom, {
        ...store.get(selectedVehiclesAtom),
        trailerCode: trailerType || undefined,
        vehicleCode: vehicleType,
      })
    }
    if (offerCode) {
      store.set(selectedOfferAtom, {
        offerCode: offerCode,
      })
    } else {
      try {
        const availableOfferCode = await getPrioritizedOffer({
          basketId: basketId ?? undefined,
          hasChildren: childrenAges.filter((a) => Number(a) > 3).length > 0,
          hasInfants: childrenAges.filter((a) => Number(a) < 4).length > 0,
          homeboundDepartureDate: depDateReturn && routeHome && routeHome !== 'ONE_WAY' ? depDateReturn : undefined,
          homeboundRouteCode: depDateReturn && routeHome && routeHome !== 'ONE_WAY' ? routeHome : undefined,
          locale: getUrlLocale(),
          outboundDepartureDate: depDateOut,
          outboundRouteCode: routeOut,
          sessionId: sessionId ?? undefined,
          trailerTypeCode: trailerType || undefined,
          vehicleCode: vehicleType || undefined,
        })

        if (availableOfferCode) {
          store.set(selectedOfferAtom, {
            offerCode: availableOfferCode,
          })
        }
      } catch {
        // Silent error handling - continue without setting an offer
      }
    }
    if (showNumberOfPets) {
      store.set(selectedPetsAtom, {
        ...selectedPets,
        showNumberOfPets: Number(showNumberOfPets),
      })
    } else {
      store.set(selectedPetsAtom, {
        pets: [],
        showNumberOfPets: 0,
      })
    }
  } else if (!window.location.search && !selectedDates.outboundDepartureDate) {
    // We use location href when going back to minibook
    window.location.href = location.pathname.split('/').slice(0, -1).join('/')
  } else if (!window.location.search && selectedDates.outboundDepartureDate && getSessionBasket()?.basketId) {
    if (selectedDates.returnDepartureDate && isDateInThePast(selectedDates.returnDepartureDate)) {
      depDateHome = presentDay
    }
    if (selectedDates.outboundDepartureDate && isDateInThePast(selectedDates.outboundDepartureDate)) {
      depDateOut = presentDay
    }
    store.set(selectedDateAtom, {
      ...store.get(selectedDateAtom),
      outboundDepartureDate: depDateOut ?? selectedDates.outboundDepartureDate,
      returnDepartureDate: depDateHome ?? selectedDates.returnDepartureDate,
    })
    homeboundRoute = selectedRoutes.homebound
    homeboundDepartureDate = selectedDates.returnDepartureDate
  } else {
    // We use location href when going back to minibook
    window.location.href = location.pathname.split('/').slice(0, -1).join('/')
  }
  sessionStorage['yourBookingUrl'] = JSON.stringify(window.location.pathname)
  if (minibookId) {
    sessionStorage['tsMinibookId'] = minibookId
  }
  if (travelSearchId) {
    sessionStorage['tsTravelSearchId'] = travelSearchId
  }
  // The URL is wiped once it has been read, so keep the discount around for the rest of the flow.
  // A recovered basket has no `commercialDiscount` query parameter and seeds its own session value.
  if (routeOut) setSessionCommercialDiscount(commercialDiscount)
  return {
    commercialDiscount: recoveredCommercialDiscount ?? commercialDiscount,
    depDateHomebound: homeboundDepartureDate,
    recoveredBasketErrors: recoveredBasketErrors,
    routeHomebound: homeboundRoute,
  }
}
